import clsx from 'clsx';
import type { Draft, FillBlankQ } from '../types';
import styles from '../QuizView.module.css';

type Props = {
  question: FillBlankQ;
  draft: Extract<Draft, { format: 'fill_blank' }>;
  onChange: (d: Extract<Draft, { format: 'fill_blank' }>) => void;
  disabled: boolean;
  showFeedback: boolean;
};

// "Lorem {0} ipsum {1}" → [{text}, {blank,0}, {text}, {blank,1}]
type Segment = { kind: 'text'; text: string } | { kind: 'blank'; idx: number };
function parse(sentence: string): Segment[] {
  const segs: Segment[] = [];
  const re = /\{(\d+)\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(sentence))) {
    if (m.index > last) segs.push({ kind: 'text', text: sentence.slice(last, m.index) });
    segs.push({ kind: 'blank', idx: Number(m[1]) });
    last = m.index + m[0].length;
  }
  if (last < sentence.length) segs.push({ kind: 'text', text: sentence.slice(last) });
  return segs;
}

const stripSuffix = (s: string) => s.replace(/-\d+$/, '');

export function FillBlank({ question, draft, onChange, disabled, showFeedback }: Props) {
  const segments = parse(question.sentence);

  const setBlank = (blankIdx: number, value: string) => {
    const next = draft.blanks.slice();
    next[blankIdx] = value || null;
    onChange({ format: 'fill_blank', blanks: next });
  };

  return (
    <div className={styles.fillBlank}>
      {question.instruction && (
        <p className={styles.qInstruction}>{question.instruction}</p>
      )}

      <p className={styles.sentence}>
        {segments.map((s, i) => {
          if (s.kind === 'text') return <span key={i}>{s.text}</span>;
          const value = draft.blanks[s.idx] ?? '';
          const available = question.options[s.idx]?.available ?? [];
          const correct =
            showFeedback &&
            stripSuffix(value) === question.options[s.idx]?.correct;
          const wrong = showFeedback && value && !correct;
          return (
            <select
              key={i}
              value={value}
              onChange={(e) => setBlank(s.idx, e.target.value)}
              disabled={disabled}
              className={clsx(
                styles.blankSelect,
                value && styles.blankSelectFilled,
                correct && styles.blankSelectCorrect,
                wrong && styles.blankSelectWrong
              )}
              aria-label={`Hueco ${s.idx + 1}`}
            >
              <option value="">— elige —</option>
              {available.map((opt, j) => (
                <option key={`${opt}-${j}`} value={opt}>
                  {stripSuffix(opt)}
                </option>
              ))}
            </select>
          );
        })}
      </p>

      {showFeedback && (
        <div className={styles.correctAnswers}>
          <span className={styles.correctAnswersLabel}>Respuestas correctas</span>
          <p>
            {question.options
              .map((o, i) => `${i + 1}. ${o.correct}`)
              .join('  ·  ')}
          </p>
        </div>
      )}
    </div>
  );
}
